import React, { useEffect, useState } from 'react';
import { Transition } from '@headlessui/react';

import config from '../config';

interface IProps {
  onSelected: (value: any) => void;
}

const GeoAutoComplete: React.FC<IProps> = ({ onSelected }) => {
  const [query, setQuery] = useState<string>('');
  const [queryResult, setQueryResult] = useState<Array<any>>([]);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [selected, setSelected] = useState<boolean>(false);

  useEffect(() => {
    setShowDropdown(queryResult.length <= 0 ? false : true);
  }, [queryResult]);

  const updateQuery = (event: any) => {
    if (query != event.target.value && selected) {
      setQuery('');
      onSelected(null);
      setSelected(false);
    } else setQuery(event.target.value);

    const header = { 'Content-Type': 'application/json' };

    let path =
      'https://api.mapbox.com/geocoding/v5/mapbox.places/' +
      query +
      '.json?access_token=' +
      config.map.token;

    if (query.length > 2) {
      return fetch(path, {
        headers: header,
      })
        .then((res) => {
          if (!res.ok) throw Error(res.statusText);
          return res.json();
        })
        .then((json) => {
          setQueryResult(json.features);
        })
        .catch((err) => {
          setQueryResult([]);
        });
    } else {
      setQueryResult([]);
    }
  };

  const selectSuggestion = (place: any) => {
    setQuery(place.place_name);
    onSelected({
      street: `${place.address} ${place.text}`,
      city: place.context.filter((i: any) => i.id.includes('place'))[0]?.text,
      region: place.context.filter((i: any) => i.id.includes('region'))[0]
        ?.text,
      postal: place.context.filter((i: any) => i.id.includes('postcode'))[0]
        ?.text,
      country: place.context.filter((i: any) => i.id.includes('country'))[0]
        ?.text,
      lat: place.geometry.coordinates[1],
      lng: place.geometry.coordinates[0],
    });
    setSelected(true);
    setShowDropdown(false);
  };

  return (
    <div className="mt-1 relative z-40">
      <div className="mt-1 relative rounded-md shadow-sm">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {/* Heroicon name: outline/search */}
          <svg
            className="h-5 w-5 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <input
          onChange={updateQuery}
          value={query}
          type="text"
          name="search"
          id="search"
          className="focus:ring-primary-light focus:border-primary-light block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
          placeholder="Search for an address..."
          autoComplete="off"
          onClick={() => setShowDropdown(!showDropdown)}
        />
      </div>

      <Transition
        show={showDropdown}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        {(ref) => (
          <div
            ref={ref}
            className="absolute mt-1 w-full rounded-md bg-white shadow-lg"
          >
            <ul
              tabIndex={-1}
              className="max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
            >
              {queryResult.map((place, i) => (
                <li
                  key={i}
                  className="text-gray-900 hover:bg-gray-200 cursor-pointer select-none relative py-2 pl-8 pr-4"
                  onClick={() => selectSuggestion(place)}
                >
                  <span className="font-normal block truncate">
                    {place.place_name}
                  </span>
                  <span className="absolute inset-y-0 left-0 flex items-center pl-1.5">
                    {/* Heroicon name: solid/location-marker */}
                    <svg
                      className="h-5 w-5 text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Transition>
    </div>
  );
};

export default GeoAutoComplete;
